import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Briefcase } from "lucide-react";

export default function Register() {
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "freelancer" });
  const [loading, setLoading] = useState(false);
  const { registerEmail } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await registerEmail(form);
      toast.success("Cuenta creada");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al registrar");
    } finally {
      setLoading(false);
    }
  };

  const googleSignup = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="flex items-center justify-center p-6 md:p-12 order-2 lg:order-1">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mb-2">Crea tu cuenta</h1>
            <p className="text-muted-foreground">Empieza a ganar o contratar en minutos</p>
          </div>

          <Button
            type="button"
            variant="outline"
            onClick={googleSignup}
            className="w-full h-11 rounded-md mb-5"
            data-testid="google-signup-btn"
          >
            <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continuar con Google
          </Button>

          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-border" />
            <span className="text-xs tracking-widest uppercase text-muted-foreground">o con email</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-xs tracking-widest uppercase font-semibold">Rol</Label>
              <div className="grid grid-cols-2 gap-2 mt-1.5">
                {[
                  { v: "freelancer", l: "Quiero trabajar" },
                  { v: "client", l: "Quiero contratar" },
                ].map((opt) => (
                  <button
                    type="button"
                    key={opt.v}
                    onClick={() => setForm({ ...form, role: opt.v })}
                    data-testid={`role-option-${opt.v}`}
                    className={`h-11 rounded-md border text-sm font-medium transition-colors ${
                      form.role === opt.v ? "border-primary bg-primary/5 text-primary" : "border-border hover:bg-muted"
                    }`}
                  >
                    {opt.l}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label htmlFor="name" className="text-xs tracking-widest uppercase font-semibold">Nombre completo</Label>
              <Input id="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1.5 h-11" data-testid="register-name-input" />
            </div>
            <div>
              <Label htmlFor="email" className="text-xs tracking-widest uppercase font-semibold">Email</Label>
              <Input id="email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1.5 h-11" data-testid="register-email-input" />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs tracking-widest uppercase font-semibold">Contraseña</Label>
              <Input id="password" type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="mt-1.5 h-11" data-testid="register-password-input" />
            </div>
            <Button type="submit" disabled={loading} className="w-full h-11 rounded-full mt-2" data-testid="register-submit-btn">
              {loading ? "Creando…" : "Crear cuenta"}
            </Button>
          </form>

          <p className="text-sm text-muted-foreground text-center mt-8">
            ¿Ya tienes cuenta?{" "}
            <Link to="/login" className="text-primary font-medium hover:underline" data-testid="link-to-login">Inicia sesión</Link>
          </p>
        </div>
      </div>

      <div className="hidden lg:flex flex-col justify-between p-12 bg-foreground text-background relative overflow-hidden order-1 lg:order-2">
        <div className="grain-overlay opacity-[0.15]" />
        <Link to="/" className="flex items-center gap-2 relative z-10">
          <div className="w-8 h-8 rounded-md bg-background/20 flex items-center justify-center">
            <Briefcase className="w-4 h-4" />
          </div>
          <span className="font-heading font-bold text-lg">TaskLink</span>
        </Link>
        <div className="relative z-10">
          <h2 className="font-heading text-5xl font-bold tracking-tight leading-tight mb-4">
            Talento sin fronteras.
          </h2>
          <p className="text-background/70 max-w-md leading-relaxed">
            Miles de proyectos digitales esperando la persona correcta. La tuya.
          </p>
        </div>
        <div className="text-xs tracking-widest uppercase relative z-10 opacity-60">
          Únete al marketplace
        </div>
      </div>
    </div>
  );
}
